import { IoClose } from "react-icons/io5";
import { GiPaperClip } from "react-icons/gi";
import { BiSend } from "react-icons/bi";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  closeAppeal,
  replyToAppeal,
  resetCloseStatus,
  resetReplyStatus,
  clearErrors,
} from "../../../redux/restaurantAppeals/restaurantAppealsSlice";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

interface AppealRestaurantModalProps {
  toggleModal: () => void;
  restaurantId: string;
}

const AppealRestaurantModal = ({ toggleModal, restaurantId }: AppealRestaurantModalProps) => {
  const dispatch = useAppDispatch();
  const { appeals, closeStatus, replyStatus, error } = useAppSelector(
    (state: RootState) => state.restaurantAppeals
  );
  const [replyMessage, setReplyMessage] = useState("");

  const appeal = appeals.find((a) => a._id === restaurantId);
  const restaurantEmail = appeal?.restaurantId?._id ? `restaurant_${appeal.restaurantId._id}@example.com` : "";

  useEffect(() => {
    if (closeStatus === "success") {
      toast.success("Appeal closed successfully");
      dispatch(resetCloseStatus());
      toggleModal();
    } else if (closeStatus === "failed" && error) {
      toast.error(error);
      dispatch(clearErrors());
    }
  }, [closeStatus, error, dispatch, toggleModal]);

  useEffect(() => {
    if (replyStatus === "success") {
      toast.success("Reply sent successfully");
      dispatch(resetReplyStatus());
      setReplyMessage("");
    } else if (replyStatus === "failed" && error) {
      toast.error(error);
      dispatch(clearErrors());
    }
  }, [replyStatus, error, dispatch]);

  const handleCloseAppeal = () => {
    if (!appeal) return;
    if (window.confirm("Are you sure you want to close this appeal?")) {
      dispatch(closeAppeal(appeal._id));
    }
  };

  const handleSendReply = () => {
    if (!appeal) return;
    if (!replyMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    dispatch(
      replyToAppeal({
        appealId: appeal.appealId,
        message: replyMessage,
        restaurantEmail: restaurantEmail,
      })
    );
  };

  // Function to format email messages by removing email headers
  const formatEmailMessage = (message: string): string => {
    // Remove email forward/reply patterns
    const lines = message.split("\n");
    const cleanedLines: string[] = [];
    let skipMode = false;

    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Skip email headers like "On Wed, 22 Oct 2025 at..."
      if (trimmedLine.startsWith("On ") && trimmedLine.includes(" wrote:")) {
        skipMode = true;
        continue;
      }
      
      // Skip quoted lines that start with >
      if (trimmedLine.startsWith(">")) {
        continue;
      }
      
      // Skip separator lines
      if (trimmedLine === "------------------------------" || trimmedLine === "---") {
        continue;
      }
      
      // Skip empty lines at the beginning
      if (cleanedLines.length === 0 && trimmedLine === "") {
        continue;
      }
      
      // If we hit actual content, turn off skip mode
      if (skipMode && trimmedLine && !trimmedLine.startsWith(">") && !trimmedLine.startsWith("On ")) {
        skipMode = false;
      }
      
      if (!skipMode && trimmedLine) {
        cleanedLines.push(trimmedLine);
      }
    }

    return cleanedLines.join(" ").trim() || message;
  };

  if (!appeal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-start p-6 pb-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-secondary">
              {appeal.restaurantId?.businessInformation?.businessName || "N/A"}
            </h2>
            <p className="text-gray-500 text-sm">Appeal ID: {appeal.appealId}</p>
          </div>
          <button
            onClick={toggleModal}
            className="text-gray-400 hover:text-gray-600"
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
          <div>
            <p className="font-semibold text-sm mb-2">Appeal Reason</p>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm">{appeal.reason}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-sm">Date Submitted:</p>
              <p className="text-gray-600 text-sm">
                {new Date(appeal.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
            <div>
              <p className="font-semibold text-sm">Status:</p>
              <p
                className={`text-sm ${
                  appeal.status === "open" ? "text-orange-500" : "text-green-500"
                }`}
              >
                {appeal.status === "open" ? "Under Review" : "Closed"}
              </p>
            </div>
          </div>

          {appeal.messages.length > 0 && (
            <div>
              <p className="font-semibold text-sm mb-2">Messages</p>
              <div className="max-h-60 overflow-y-auto space-y-3">
                {appeal.messages.map((msg) => (
                  <div
                    key={msg._id}
                    className={`p-3 rounded-md ${
                      msg.senderType === "admin"
                        ? "bg-blue-50 border-l-4 border-blue-500"
                        : "bg-gray-50 border-l-4 border-gray-500"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-semibold uppercase">
                        {msg.senderType}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(msg.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {formatEmailMessage(msg.message)}
                    </p>
                    {msg.attachments.length > 0 && (
                      <div className="mt-2">
                        {msg.attachments.map((att) => (
                          <a
                            key={att._id}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-secondary text-xs underline"
                          >
                            <GiPaperClip className="w-3 h-3 mr-1" />
                            {att.filename}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {appeal.status === "open" && (
            <div>
              <p className="font-semibold text-sm mb-2">Send Reply</p>
              <textarea
                className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-secondary"
                rows={4}
                placeholder="Type your reply to the restaurant..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                disabled={replyStatus === "loading"}
              />
              <button
                onClick={handleSendReply}
                disabled={replyStatus === "loading" || !replyMessage.trim()}
                className="mt-2 flex items-center gap-2 bg-secondary text-white py-2 px-4 rounded-md hover:bg-secondary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <BiSend className="w-4 h-4" />
                {replyStatus === "loading" ? "Sending..." : "Send Reply"}
              </button>
            </div>
          )}
          </div>
        </div>

        <div className="border-t p-6 pt-4">
          <div className="flex gap-3">
            {appeal.status === "open" && (
              <button
                onClick={handleCloseAppeal}
                disabled={closeStatus === "loading"}
                className="flex-1 bg-secondary text-white py-3 px-4 rounded-md hover:bg-secondary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {closeStatus === "loading" ? "Closing..." : "Close Appeal"}
              </button>
            )}
            <button
              onClick={toggleModal}
              className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppealRestaurantModal;
